import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  CssBaseline,
  Box,
  Typography,
  Divider,
  useMediaQuery,
  IconButton,
  AppBar,
  Button,
  Container,
  Badge,
  Snackbar,
  Alert,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import React, { useState, useEffect, useRef } from "react";
import logoSvg from "../assets/TowHelp logo-07.svg";
import { getBookings } from "../services/api/getAll";

const drawerWidth = 280;

const DashboardLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:900px)");
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Notification states
  const [pendingCount, setPendingCount] = useState(0);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  
  const totalBookingsCountRef = useRef<number | null>(null);

  const fetchNotificationData = async () => {
    try {
      const res: any = await getBookings();
      if (res && res.success === false) {
        // If Firebase fetch failed, do not update notifications with incomplete data
        return;
      }
      const items = res?.items || [];
      
      // Count pending bookings
      const pending = items.filter((b: any) => b.status === "Pending" || !b.status).length;
      setPendingCount(pending);

      // Check if total count increased (new booking received)
      if (totalBookingsCountRef.current !== null && items.length > totalBookingsCountRef.current) {
        // Trigger alerts
        playChime();
        flashBrowserTab();
        setToastMessage("🔔 New Booking Received!");
        setToastOpen(true);
      }
      totalBookingsCountRef.current = items.length;
    } catch (e) {
      console.error("Failed to fetch bookings for notifications:", e);
    }
  };

  const playChime = () => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      
      const playTone = (freq: number, start: number, duration: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, start);
        gain.gain.setValueAtTime(0.35, start);
        gain.gain.exponentialRampToValueAtTime(0.01, start + duration);
        osc.start(start);
        osc.stop(start + duration);
      };
      
      // Play a lovely double notification ringtone
      playTone(587.33, ctx.currentTime, 0.2); // D5
      playTone(783.99, ctx.currentTime + 0.15, 0.45); // G5
    } catch (err) {
      console.log("Audio alert blocked by browser autocomplete:", err);
    }
  };

  const flashBrowserTab = () => {
    let toggle = false;
    const originalTitle = document.title;
    const interval = setInterval(() => {
      document.title = toggle ? "🔔 (1) New Booking!" : originalTitle;
      toggle = !toggle;
    }, 1000);

    const stopFlasher = () => {
      clearInterval(interval);
      document.title = originalTitle;
      window.removeEventListener("mousemove", stopFlasher);
      window.removeEventListener("click", stopFlasher);
    };

    window.addEventListener("mousemove", stopFlasher);
    window.addEventListener("click", stopFlasher);
  };

  useEffect(() => {
    fetchNotificationData();
    // Poll every 10 seconds for real-time notifications
    const interval = setInterval(fetchNotificationData, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const menuItems = [
    { label: "Bookings", path: "BookingsAdminScreen", badge: pendingCount },
    { label: "Promo & Pricing", path: "PromoPricingAdminScreen" },
    { label: "Banners", path: "BannersAdminScreen" },
    { label: "About Us", path: "about" },
    { label: "Services", path: "ServicesAdminScreen" },
    { label: "Crew", path: "CrewAdminScreen" },
    { label: "Mission & Vision", path: "MissionVisionForm" },
    { label: "Join Requests", path: "RequestsAdminScreen" },
    { label: "Contact Us", path: "ContactUsAdmin" },
    { label: "Modify Users", path: "ModifyUserScreen" },
    { label: "Manage Links", path: "BrandsAdminScreen" },
  ];

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "#0f0f0f", color: "#fff" }}>
      <Box sx={{ px: 3, py: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
        <img src={logoSvg} alt="TowHelp Logo" style={{ height: 38 }} />
        <Typography
          variant="h6"
          fontWeight="900"
          fontFamily="Poppins"
          sx={{
            background: "linear-gradient(135deg, #FFAE00 0%, #FF8F00 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            letterSpacing: "1px"
          }}
        >
          TowHelp Admin
        </Typography>
      </Box>
      <Divider sx={{ bgcolor: "rgba(255,174,0,0.15)" }} />
      
      <List sx={{ flexGrow: 1, px: 2, pt: 2, overflowY: "auto" }}>
        {menuItems.map((item) => {
          const isActive = location.pathname.includes(item.path);
          return (
            <ListItem
              key={item.path}
              disablePadding
              sx={{
                mb: 0.8,
                borderRadius: "12px",
                backgroundColor: isActive ? "rgba(255, 174, 0, 0.12)" : "transparent",
                borderLeft: isActive ? "4px solid #FFAE00" : "4px solid transparent",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "rgba(255, 174, 0, 0.05)",
                },
              }}
            >
              <ListItemButton
                component={Link}
                to={`/dashboard/${item.path}`}
                sx={{
                  borderRadius: "12px",
                  py: 1.2,
                  px: 2,
                }}
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive ? "800" : 500,
                    fontFamily: "Poppins",
                    color: isActive ? "#FFAE00" : "#e0e0e0",
                    fontSize: "0.95rem"
                  }}
                />
                {item.badge !== undefined && item.badge > 0 && (
                  <Badge
                    badgeContent={item.badge}
                    color="error"
                    sx={{
                      "& .MuiBadge-badge": {
                        background: "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
                        fontWeight: "bold",
                        boxShadow: "0 0 10px rgba(244, 67, 54, 0.5)",
                      }
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      
      <Divider sx={{ bgcolor: "rgba(255,255,255,0.08)" }} />
      
      <Box sx={{ px: 3, py: 3.5 }}>
        <Button
          onClick={handleLogout}
          variant="outlined"
          size="medium"
          sx={{
            textTransform: "none",
            fontWeight: "bold",
            width: "100%",
            color: "#FFAE00",
            borderColor: "#FFAE00",
            borderRadius: "10px",
            transition: "all 0.3s ease",
            "&:hover": {
              backgroundColor: "rgba(255, 174, 0, 0.08)",
              borderColor: "#FF8F00",
              color: "#FF8F00",
            },
          }}
        >
          Logout
        </Button>
      </Box>
      <Box sx={{ px: 3, pb: 2.5, textAlign: "center", fontSize: 11, color: "#666" }}>
        © {new Date().getFullYear()} TowHelp Premium Admin
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", fontFamily: "Poppins", bgcolor: "#080808", color: "#fff", minHeight: "100vh" }}>
      <CssBaseline />

      {isMobile ? (
        <>
          <AppBar
            position="fixed"
            sx={{
              width: "100%",
              backgroundColor: "#0f0f0f",
              borderBottom: "1px solid rgba(255,174,0,0.15)",
              boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
              zIndex: 1201,
              color: "#fff",
            }}
          >
            <Toolbar sx={{ justifyContent: "space-between" }}>
              <Box display="flex" alignItems="center" gap={1.5}>
                <img src={logoSvg} alt="TowHelp Logo" style={{ height: 32 }} />
                <Typography variant="h6" fontWeight="bold" sx={{ color: "#FFAE00" }}>
                  TowHelp
                </Typography>
              </Box>
              <IconButton edge="end" onClick={() => setMobileOpen(!mobileOpen)} color="inherit">
                <MenuIcon sx={{ color: "#FFAE00" }} />
              </IconButton>
            </Toolbar>
          </AppBar>

          <Drawer
            anchor="left"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              "& .MuiDrawer-paper": {
                width: drawerWidth,
                boxSizing: "border-box",
                backgroundColor: "#0f0f0f",
                color: "#fff",
                borderRight: "1px solid rgba(255,174,0,0.15)",
              },
            }}
          >
            {drawerContent}
          </Drawer>
        </>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: drawerWidth,
              boxSizing: "border-box",
              backgroundColor: "#0f0f0f",
              color: "#fff",
              borderRight: "1px solid rgba(255,174,0,0.15)",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "#080808",
          minHeight: "100vh",
          pb: 4,
          pt: isMobile ? 10 : 0,
          minWidth: 0,
        }}
      >
        <Container maxWidth={false} sx={{ pt: isMobile ? 0 : 4, px: { xs: 1, sm: 2, md: 3 } }}>
          <Outlet />
        </Container>
      </Box>

      {/* Global Real-time Booking alert toast */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity="success"
          onClose={() => setToastOpen(false)}
          sx={{
            bgcolor: "#1e1e1e",
            color: "#fff",
            border: "1px solid #FFAE00",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(255, 174, 0, 0.2)",
            "& .MuiAlert-icon": { color: "#FFAE00" },
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DashboardLayout;
